/**
 * Full response — the read-only reader for a multi-section response (spec 29 §8).
 *
 * Reading-first: an outline rail, a 120px workflow margin beside a ~700px
 * prose column, and the Ask AI panel closed behind a rail. The outline and the
 * open panel resize like the section editor's and share its stored widths. It is a mode you
 * step into from the sections overview, not a sibling tab of it. v1 does not edit in place;
 * "Edit section" hands off to the section editor, and the AI panel is comments
 * only, so nothing on this page can change the draft.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, ChevronRight, Eye, Loader2, MessageSquareText, Sparkles } from 'lucide-react'
import { format } from 'date-fns'
import { toastExportResult } from '@/lib/pending-ai-change-notices'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ArtifactPageHeader } from '@/components/ArtifactPageHeader'
import { ArtifactStatusBadge } from '@/components/ArtifactStatusBadge'
import { ExportDropdown } from '@/components/ExportDropdown'
import { ExportGapConfirmDialog } from '@/components/drafting/ExportGapConfirmDialog'
import { useExportGapCheck } from '@/hooks/useExportGapCheck'
import { ReviewDecisionDialog } from '@/components/ReviewDecisionDialog'
import { DraftStatsFooter } from '@/components/drafting/DraftStatsFooter'
import { collectCitedSources, findDraftToDos, type CitingContent } from '@/lib/draft-stats'
import { citingParts } from '@/components/full-response/citation-markers'
import { UnifiedAiPanel } from '@/components/drafting/UnifiedAiPanel'
import type { CommentJumpContext } from '@/components/drafting/CommentCard'
import { useFullResponseStore } from '@/stores/full-response-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useSectionAssignmentsStore } from '@/stores/section-assignments-store'
import { useArtifactReviewsStore } from '@/stores/artifact-reviews-store'
import { useReviewStore } from '@/stores/review-store'
import { useAuthStore } from '@/stores/auth-store'
import { useBidMembers } from '@/hooks/useBidMembers'
import { useCommentsPolling } from '@/hooks/useCommentsPolling'
import { useResizablePanes } from '@/hooks/useResizablePanes'
import { ResizeHandle } from '@/components/ui/ResizeHandle'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'
import { exportArtifactToDocx, toastApiError } from '@/lib/api'
import type { ArtifactReview, ArtifactReviewDecision, ExportOptions, FullResponseSection as Section } from '@/lib/api'
import { routeTemplateExportError } from '@/stores/export-templates-store'
import { usePdfExportStore } from '@/stores/pdf-export-store'
import type { CollaborationStatusKey } from '@/lib/section-status'
import { keepCriteriaForScope, type MergedReviewFinding } from '@/lib/schemas'
import { canEditSection, sectionDomId } from './provenance'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { FullResponseOutline } from './FullResponseOutline'
import { FullResponseSection } from './FullResponseSection'
import { CheckStatusLine } from './CheckStatusLine'
import { FullResponseChecksTab } from './FullResponseChecksTab'

// Stable identities: a fresh [] from a zustand selector re-renders forever.
const EMPTY_REVIEWS: ArtifactReview[] = []
const EMPTY_SECTIONS: Section[] = []
const NO_ISSUES: MergedReviewFinding[] = []
const CHECK_POLL_MS = 3000
/** The closed Ask AI rail is `w-10`; counted against the reading column's minimum. */
const CLOSED_PANEL_RAIL_WIDTH = 40

export function FullResponsePage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const navigate = useNavigate()

  const currentUserId = useAuthStore((s) => s.user?.id)
  const bidMembers = useBidMembers(bidId)
  const isOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
  // A read-only user (an org viewer, or a member who is not on the bid) gets
  // no way into the editor: it would open read-only.
  const { isReadOnly } = useBidReadOnly(bidId)

  const {
    selectedArtifact: artifact,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
  } = useResponsesStore()
  // `document` would shadow window.document, which the scroll handlers need.
  const { document: response, isLoading, error, load, reset } = useFullResponseStore()
  const { assignments, fetchAssignments } = useSectionAssignmentsStore()
  const reviews = useArtifactReviewsStore(
    (s) => (artifactId ? s.byArtifactId[artifactId] : undefined) ?? EMPTY_REVIEWS,
  )
  const loadReviews = useArtifactReviewsStore((s) => s.load)

  useCommentsPolling(bidId, artifactId)

  const [evaluatorView, setEvaluatorView] = useState(false)
  // Closed by default — this is a reading surface first (spec 29 §0.6.1).
  const [panelOpen, setPanelOpen] = useState(false)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [confirmDecision, setConfirmDecision] = useState<ArtifactReviewDecision | null>(null)
  const [panelTab, setPanelTab] = useState<'review' | 'comment'>('comment')
  const [highlight, setHighlight] = useState<{ sectionKey: string; excerpt: string | null } | null>(null)
  const [isStartingCheck, setIsStartingCheck] = useState(false)

  const {
    containerRef: panesRef,
    outlineWidth,
    aiPanelWidth,
    isOutlineResizing,
    isAiPanelResizing,
    onOutlineResizeStart,
    onAiPanelResizeStart,
  } = useResizablePanes({
    isAiPanelOpen: panelOpen,
    fixedWidth: panelOpen ? 0 : CLOSED_PANEL_RAIL_WIDTH,
  })

  const activeFindings = useReviewStore((s) => s.activeFindings)
  const latestCheck = useReviewStore((s) => s.latestCheck)
  const criteria = useReviewStore((s) => s.criteria)
  const loadLatestCheck = useReviewStore((s) => s.loadLatestCheck)
  const loadActiveFindings = useReviewStore((s) => s.loadActiveFindings)
  const loadCriteria = useReviewStore((s) => s.loadCriteria)
  const startReview = useReviewStore((s) => s.startReview)

  useEffect(() => {
    if (!artifactId) return
    void fetchArtifactById(artifactId)
    void load(artifactId)
    void fetchAssignments(artifactId)
    void loadReviews(artifactId)
    void loadActiveFindings(artifactId)
    void loadLatestCheck(artifactId)
    void loadCriteria()
    return () => reset()
  }, [
    artifactId,
    fetchArtifactById,
    load,
    fetchAssignments,
    loadReviews,
    loadActiveFindings,
    loadLatestCheck,
    loadCriteria,
    reset,
  ])

  // A check runs in a worker; poll the summary until it settles, then pull the
  // findings once so the margin markers and the Checks tab agree.
  const runStatus = latestCheck?.run?.status ?? null
  const prevRunStatus = useRef<string | null>(null)
  useEffect(() => {
    if (!artifactId || runStatus !== 'running') return
    const timer = window.setInterval(() => {
      void loadLatestCheck(artifactId)
    }, CHECK_POLL_MS)
    return () => window.clearInterval(timer)
  }, [artifactId, runStatus, loadLatestCheck])

  useEffect(() => {
    if (!artifactId) return
    if (prevRunStatus.current === 'running' && runStatus === 'complete') {
      void loadActiveFindings(artifactId)
    }
    prevRunStatus.current = runStatus
  }, [artifactId, runStatus, loadActiveFindings])

  const sections = useMemo(() => response?.sections ?? EMPTY_SECTIONS, [response])
  const totalWords = response?.totalWords ?? 0
  const toDoCount = useMemo(
    () => sections.reduce((total, section) => total + findDraftToDos(section.html).length, 0),
    [sections],
  )
  // Every document the response cites, de-duplicated across sections. Each
  // segment's refs are matched against that segment's own text — citation
  // numbers restart per segment. Undefined (the footer omits sources) when the
  // backend sent no segments and so cannot say.
  const sources = useMemo(() => {
    const parts: CitingContent[] = []
    for (const section of sections) {
      const sectionParts = citingParts(section)
      if (sectionParts === null) return undefined
      parts.push(...sectionParts)
    }
    return collectCitedSources(parts)
  }, [sections])
  // The guards below render a spinner until both calls land, so no section
  // element exists yet. `sections` alone cannot express that: its identity does
  // not change when the artifact arrives second, so an effect keyed only on it
  // would attach to nothing and never re-run.
  const ready = Boolean(artifact && response)

  const assignmentByKey = useMemo(
    () => new Map(assignments.map((a) => [a.sectionId, a])),
    [assignments],
  )
  const statusByKey = useMemo(
    () => new Map<string, CollaborationStatusKey>(assignments.map((a) => [a.sectionId, a.collaborationStatus])),
    [assignments],
  )

  const issuesBySection = useMemo(() => {
    const map = new Map<string, MergedReviewFinding[]>()
    for (const finding of activeFindings ?? []) {
      if (!finding.sectionId) continue
      const list = map.get(finding.sectionId)
      if (list) list.push(finding)
      else map.set(finding.sectionId, [finding])
    }
    return map
  }, [activeFindings])
  const issueCount = activeFindings?.length ?? 0

  const handleCheckAgain = useCallback(() => {
    if (!bidId || !artifactId) return
    const previous = latestCheck?.run?.criteriaIds ?? []
    const known = [...(criteria?.system ?? []), ...(criteria?.mine ?? [])]
    const criteriaIds =
      previous.length > 0
        ? previous
        : keepCriteriaForScope(
            (criteria?.system ?? []).map((c) => c.id),
            known,
            'artifact',
          )
    if (criteriaIds.length === 0) return
    setIsStartingCheck(true)
    void (async () => {
      await startReview({ bidId, artifactId, sectionKey: null, criteriaIds })
      await loadLatestCheck(artifactId)
      setIsStartingCheck(false)
    })()
  }, [bidId, artifactId, latestCheck, criteria, startReview, loadLatestCheck])

  const pendingReview = reviews.find((r) => r.status === 'pending') ?? null
  const inFinalReview = artifact?.status === 'pending_review' && pendingReview !== null
  const isFinalReviewer = inFinalReview && pendingReview?.reviewerId === currentUserId

  // Active section tracking. jsdom has no IntersectionObserver, so guard it —
  // the outline still highlights on click without one.
  useEffect(() => {
    if (!ready || typeof IntersectionObserver === 'undefined' || sections.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        const key = visible?.target.getAttribute('data-section-key')
        if (key) setActiveKey(key)
      },
      { rootMargin: '-10% 0px -70% 0px' },
    )
    sections.forEach((s) => {
      const el = window.document.getElementById(sectionDomId(s.sectionKey))
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [sections, ready])

  const handleSelect = (key: string) => {
    setActiveKey(key)
    // `behavior: 'smooth'` measured as a no-op on the doc column's scrollTop
    // in the real browser — 'auto' actually scrolls, and also respects
    // prefers-reduced-motion users for free.
    window.document.getElementById(sectionDomId(key))?.scrollIntoView({ block: 'start', behavior: 'auto' })
  }

  const handleIssueJump = useCallback((sectionKey: string, excerpt: string | null) => {
    setHighlight({ sectionKey, excerpt })
    setActiveKey(sectionKey)
    window.document
      .getElementById(sectionDomId(sectionKey))
      ?.scrollIntoView({ block: 'start', behavior: 'auto' })
  }, [])

  // The reading column's own chips are the targets (FullResponseSection wraps
  // each to-do in the editor's chip), so the first one in the DOM is the first
  // to-do in the response.
  const readingRef = useRef<HTMLDivElement | null>(null)
  const handleJumpToFirstToDo = useCallback(() => {
    readingRef.current
      ?.querySelector('[data-annotation-type]')
      ?.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [])

  const handleIssueMarkerClick = useCallback((sectionKey: string) => {
    setPanelOpen(true)
    setPanelTab('review')
    setHighlight({ sectionKey, excerpt: null })
  }, [])

  // Either call can be the one that failed, and each store clears only its own
  // error — retrying just the document would leave an artifact failure stuck.
  const handleRetry = () => {
    if (!artifactId) return
    void fetchArtifactById(artifactId)
    void load(artifactId)
  }

  const handleEdit = (key: string) => {
    void navigate(`/app/bids/${bidId ?? ''}/responses/${artifactId ?? ''}/sections/${key}`)
  }

  const { runWithGapCheck, exportGapDialogProps } = useExportGapCheck({
    artifactId,
    outlineSections: sections,
    fallbackTitle: artifact?.name ?? 'This document',
    onReviewGaps: (sectionKey) => {
      if (sectionKey) handleEdit(sectionKey)
    },
  })

  const runExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      // R3: a PDF is converted from the export's exact Word file; the export
      // menu shows its progress and result.
      const isPdf = options?.format === 'pdf'
      const { pendingAiChanges } = isPdf
        ? await usePdfExportStore.getState().exportPdf(artifactId, templateId, options)
        : await exportArtifactToDocx(artifactId, undefined, templateId, options)
      toastExportResult(isPdf ? 'PDF exported successfully' : 'Document exported successfully', pendingAiChanges)
    } catch (err) {
      // A template that would replace its own pages asks first (D4) — for a
      // PDF exactly as for Word, so the retry keeps the format.
      const retry = () => void runExport(templateId, { ...options, acknowledgeTemplateLoss: true })
      if (!routeTemplateExportError(err, retry)) toastApiError(err, 'Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  const handleExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      await runWithGapCheck(() => runExport(templateId, options))
    } finally {
      setIsExporting(false)
    }
  }

  // Every section is on screen here, so a comment jump never leaves the page.
  const commentJumpContext = useMemo<CommentJumpContext>(
    () => ({
      editorMode: 'composite',
      currentSectionKey: null,
      buildSectionPath: () => null,
      navigate: (path) => void navigate(path),
    }),
    [navigate],
  )

  const spinner = (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )

  if (artifactLoading || (isLoading && !response)) return spinner

  const failure = artifactError ?? error
  if (failure) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <AlertCircle className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{failure}</p>
        <Button variant="outline" size="sm" onClick={handleRetry}>
          Try again
        </Button>
      </div>
    )
  }

  if (!artifact) return spinner

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* The header's own seam: nothing below it draws a top edge. */}
      <div className="border-b border-border">
        <ArtifactPageHeader
          title={artifact.name}
          description={artifact.description}
          onBack={() => void navigate(`/app/bids/${bidId ?? ''}/responses/${artifactId ?? ''}`)}
          backLabel="Back to sections"
          titleBadges={<ArtifactStatusBadge status={artifact.status} />}
          actions={
            <>
              <ExportDropdown
                artifactId={artifactId}
                disabled={isExporting || sections.length === 0}
                isExporting={isExporting}
                onExport={handleExport}
              />
              {isFinalReviewer && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDecision('request_changes')}>
                    <MessageSquareText className="h-4 w-4 mr-2" />
                    Request changes
                  </Button>
                  <Button size="sm" onClick={() => setConfirmDecision('approve')}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </>
              )}
            </>
          }
        />
      </div>

      <div ref={panesRef} className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-h-0 shrink-0 overflow-hidden" style={{ width: outlineWidth }} data-outline-pane="true">
          <FullResponseOutline
            sections={sections}
            statusByKey={statusByKey}
            activeKey={activeKey}
            onSelect={handleSelect}
          />
        </div>

        {/* The handle's hairline is the seam between outline and reading column. */}
        <ResizeHandle
          onMouseDown={onOutlineResizeStart}
          isResizing={isOutlineResizing}
          aria-label="Resize outline"
        />

        {/* `min-w-0`: the reading column takes the remainder without pushing the
            row into a horizontal overflow; the side panes are what get clamped. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background" data-reader-pane="true">
          <div className="border-b border-border">
            <div className="mx-auto flex h-10 w-full max-w-[848px] items-center gap-3 px-2 text-meta">
              <span data-testid="full-response-status-line" className="flex items-center gap-1.5">
                {inFinalReview && pendingReview ? (
                  <>
                    <Eye className="h-3.5 w-3.5" />
                    <span>
                      Final review · <span className="font-medium text-foreground">Round {pendingReview.round}</span> ·
                      requested by{' '}
                      {pendingReview.requestedByFullName ?? pendingReview.requestedByEmail ?? 'the owner'},{' '}
                      {format(new Date(pendingReview.requestedAt), 'd MMM')}
                    </span>
                  </>
                ) : null}
              </span>
              <CheckStatusLine
                latest={latestCheck}
                issueCount={issueCount}
                isStarting={isStartingCheck}
                onCheckAgain={handleCheckAgain}
                onRetry={handleCheckAgain}
              />
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'ml-auto',
                  evaluatorView && 'bg-foreground/[0.07] text-foreground font-medium'
                )}
                aria-pressed={evaluatorView}
                onClick={() => setEvaluatorView((v) => !v)}
                title="Shows the document as it will be exported"
              >
                <Eye className="h-4 w-4 mr-1.5" />
                Preview
              </Button>
            </div>
          </div>

          <div ref={readingRef} className="min-h-0 flex-1 overflow-y-auto px-4 pt-5 pb-16">
            {sections.map((section, index) => {
              const assignment = assignmentByKey.get(section.sectionKey)
              return (
                <FullResponseSection
                  key={section.sectionKey}
                  index={index}
                  section={section}
                  assignment={assignment}
                  canEdit={canEditSection({
                    artifactStatus: artifact.status,
                    isOwner,
                    assignment,
                    currentUserId,
                    isReadOnly,
                  })}
                  evaluatorView={evaluatorView}
                  onEdit={handleEdit}
                  issues={evaluatorView ? NO_ISSUES : issuesBySection.get(section.sectionKey) ?? NO_ISSUES}
                  highlightExcerpt={highlight?.sectionKey === section.sectionKey ? highlight.excerpt : null}
                  onIssueMarkerClick={handleIssueMarkerClick}
                />
              )
            })}
          </div>

          {/* Totals across the whole response, same line as the section
              editor's footer. Stays in Preview: the numbers describe the
              response, not the view — but the to-dos only jump in the working
              view, where they are on screen. The header no longer repeats the
              word count. */}
          <div className="shrink-0 border-t border-border">
            <div className="mx-auto flex h-9 w-full max-w-[848px] items-center justify-end px-2">
              <DraftStatsFooter
                words={`${totalWords.toLocaleString()} words`}
                toDos={{ count: toDoCount, onJump: evaluatorView ? undefined : handleJumpToFirstToDo }}
                sources={sources}
              />
            </div>
          </div>
        </div>

        {panelOpen && (
          <ResizeHandle
            onMouseDown={onAiPanelResizeStart}
            isResizing={isAiPanelResizing}
            aria-label="Resize AI panel"
          />
        )}

        {panelOpen ? (
          <div
            className="flex min-h-0 shrink-0 flex-col overflow-hidden bg-muted/25"
            style={{ width: aiPanelWidth }}
            data-ai-pane="true"
          >
            <div className="flex items-center justify-end px-2 pt-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Close Ask AI"
                onClick={() => setPanelOpen(false)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <UnifiedAiPanel
              key={artifactId}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={DOCUMENT_SECTION_KEY}
              scopeKind="artifact"
              scopeLabel="This response"
              bidMembers={bidMembers}
              commentJumpContext={commentJumpContext}
              commentsOnly
              reviewerTabs
              initialTab={panelTab}
              renderChecks={() => (
                <FullResponseChecksTab
                  bidId={bidId ?? ''}
                  artifactId={artifactId ?? ''}
                  artifactStatus={artifact.status}
                  isOwner={isOwner}
                  sections={sections}
                  assignmentByKey={assignmentByKey}
                  currentUserId={currentUserId}
                  onJump={handleIssueJump}
                />
              )}
            />
          </div>
        ) : (
          <aside className="flex w-10 shrink-0 flex-col items-center gap-3.5 border-l border-border bg-muted/25 py-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-[hsl(var(--brand))]"
              aria-label="Open Ask AI"
              onClick={() => setPanelOpen(true)}
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            <span className="text-meta tracking-[.02em] [writing-mode:vertical-rl]">
              Ask AI
            </span>
          </aside>
        )}
      </div>

      <ExportGapConfirmDialog {...exportGapDialogProps} />

      {pendingReview && (
        <ReviewDecisionDialog
          artifact={artifact}
          review={pendingReview}
          decision={confirmDecision}
          onClose={() => setConfirmDecision(null)}
        />
      )}
    </div>
  )
}
